/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2023-12-08 21:54:28
 */

import React, { createContext, useContext, useReducer, Dispatch, ReactNode } from 'react'
import dayjs, { Dayjs } from 'dayjs'

// 类型定义
export interface AIPhotoFilter {
  labels: string[]
  times: [Dayjs, Dayjs]
}

export interface AIPhotoState {
  photos: any[]
  filter: AIPhotoFilter
  lastPageCond: string
  checkedPhotos: Map<string, any>
  unCheckedPhotos: Map<string, any>
  checkAll: boolean
  photosNum: number
}

export type AIPhotoAction =
  | { type: 'setPhotos'; data: any[] }
  | { type: 'setLastPageCond'; data: string }
  | { type: 'pushCheckedPhotos'; data: { seq: string; [key: string]: any } }
  | { type: 'pushUnCheckedPhotos'; data: { seq: string; [key: string]: any } }
  | { type: 'removeCheckedPhotos'; data: { seq: string } }
  | { type: 'removeUnCheckedPhotos'; data: { seq: string } }
  | { type: 'clearCheckedPhotos' }
  | { type: 'clearUnCheckedPhotos' }
  | { type: 'setCheckAll'; data: boolean }
  | { type: 'setFilter'; data: AIPhotoFilter }

export const initialState: AIPhotoState = {
  photos: [],
  filter: {
    labels: [],
    times: [
      dayjs().add(-14, 'day').startOf('day'),
      dayjs().add(-1, 'day').endOf('day')
    ]
  },
  lastPageCond: '',
  checkedPhotos: new Map(),
  unCheckedPhotos: new Map(),
  checkAll: false,
  photosNum: 2
}

const reducer = (state: AIPhotoState, action: AIPhotoAction): AIPhotoState => {
  switch (action.type) {
    case 'setPhotos':
      return {
        ...state,
        photos: action.data
      }
    case 'setLastPageCond':
      return {
        ...state,
        lastPageCond: action.data
      }
    case 'pushCheckedPhotos':
      return {
        ...state,
        checkedPhotos: new Map(state.checkedPhotos).set(
          action.data.seq,
          action.data
        )
      }
    case 'pushUnCheckedPhotos':
      return {
        ...state,
        unCheckedPhotos: new Map(state.unCheckedPhotos).set(
          action.data.seq,
          action.data
        )
      }
    case 'removeCheckedPhotos': {
      const newCheckedPhotos = new Map(state.checkedPhotos)
      newCheckedPhotos.delete(action.data.seq)
      return {
        ...state,
        checkedPhotos: newCheckedPhotos
      }
    }
    case 'removeUnCheckedPhotos': {
      const newUnCheckedPhotos = new Map(state.unCheckedPhotos)
      newUnCheckedPhotos.delete(action.data.seq)
      return {
        ...state,
        unCheckedPhotos: newUnCheckedPhotos
      }
    }
    case 'clearCheckedPhotos':
      return {
        ...state,
        checkedPhotos: new Map()
      }
    case 'clearUnCheckedPhotos':
      return {
        ...state,
        unCheckedPhotos: new Map()
      }
    case 'setCheckAll':
      return {
        ...state,
        checkAll: action.data
      }
    case 'setFilter':
      return {
        ...state,
        filter: action.data
      }
    default:
      return state
  }
}

const AIPhotoContext = createContext<AIPhotoState | null>(null)
const DispatchContext = createContext<Dispatch<AIPhotoAction> | null>(null)

interface AIPhotoProviderProps {
  children: ReactNode
}

export const AIPhotoProvider = ({ children }: AIPhotoProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  return (
    <AIPhotoContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </AIPhotoContext.Provider>
  )
}

export const useAIPhoto = (): AIPhotoState => {
  const context = useContext(AIPhotoContext)
  if (context === null) {
    throw new Error('useAIPhoto must be used within a AIPhotoProvider')
  }
  return context
}

export const useAIPhotoDispatch = (): Dispatch<AIPhotoAction> => {
  const context = useContext(DispatchContext)
  if (context === null) {
    throw new Error('useAIPhotoDispatch must be used within a AIPhotoProvider')
  }
  return context
}
